import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars, elevationCss, typographyCss } from '@creditornot/cb-ingredients';
import { Textarea } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  createSftpIntegrationUser,
  appendSshKeyToIntegrationUser,
} from 'apis/merchant-gateway/pos-integration-service/sftp-integration/api';
import { WoltModal } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';

import { FormData } from './types';
import { sshKeyFormSchema } from './utils';

type ModalProps = {
  merchantId: string;
  isOpen: boolean;
  createMode: boolean;
  onClose: () => void;
};

const StyledModal = styled(WoltModal)`
  width: 100%;
  max-width: 640px;
`;

const Container = styled.div`
  ${elevationCss(16)}
  background: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const ModalButton = styled(Button)`
  margin: 0 8px;
`;

const FormContent = styled.div`
  ${typographyCss.Body3()}
  margin-block-end: 8px;
`;

const FormLabel = styled.div`
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-end: 8px;
  ${typographyCss.Small()}
`;

const ErrorMessage = styled(Alert).attrs({ variant: 'error' })`
  background-color: white;
  color: ${colors.strawberry100};
  padding: 8px 0 0;
`;

const SftpUserKeysModal = ({ onClose, isOpen, createMode, merchantId }: ModalProps) => {
  const intl = useIntl();

  const initialData = {
    sshKey: '',
  };

  const formSchema = sshKeyFormSchema(intl);
  const mutation = useMutation(
    async (data: FormData) => {
      if (createMode) {
        return await createSftpIntegrationUser(merchantId, { key: data.sshKey });
      } else {
        return await appendSshKeyToIntegrationUser(merchantId, { key: data.sshKey });
      }
    },
    {
      onSuccess: () => {
        onClose();
      },
    },
  );

  const _onSubmit = async (data: FormData) => {
    try {
      await mutation.mutateAsync(data);
      return {};
    } catch (err) {
      if (err.name === 'ValidationError' && err.type) {
        return { sshKey: err.errors[0] };
      }
      return { sshKey: intl.formatMessage({ id: 'errors.unknown-error-retry-contact' }) };
    }
  };

  return (
    <StyledModal isOpen={isOpen}>
      <Container>
        <ModalHeader onCloseClick={onClose}>
          <FormattedMessage
            id={
              createMode
                ? 'merchant-integrations.sftp.user-mgmt.create-details'
                : 'merchant-integrations.sftp.user-mgmt.add-heading'
            }
          />
        </ModalHeader>
        <Form<FormData>
          initialValues={initialData}
          onSubmit={_onSubmit}
          validate={validateYupSchema(formSchema)}
          render={({
            handleSubmit,
            pristine,
            hasValidationErrors,
            submitting,
            errors,
            dirtySinceLastSubmit,
            submitErrors,
          }) => (
            <>
              <form onSubmit={handleSubmit}>
                <ModalContent>
                  <FormContent>
                    <FormattedMessage
                      id={
                        createMode
                          ? 'merchant-integrations.sftp.user-mgmt.create-content'
                          : 'merchant-integrations.sftp.user-mgmt.add-content'
                      }
                    />
                  </FormContent>
                  <FormLabel>
                    <FormattedMessage id="merchant-integrations.sftp.ssh-key" />
                  </FormLabel>

                  <Field name="sshKey">
                    {field => (
                      <>
                        <Textarea
                          {...field.input}
                          rows={8}
                          maxLength={5000}
                          autoresizeMaxRows={10}
                          invalid={!!field.meta.error && !!field.meta.touched}
                          placeholder={intl.formatMessage({ id: 'merchant-integrations.sftp.form.input-placeholder' })}
                        />
                      </>
                    )}
                  </Field>
                  {!pristine && errors && errors.sshKey != null && <ErrorMessage>{errors.sshKey}</ErrorMessage>}
                  {!dirtySinceLastSubmit && submitErrors && <ErrorMessage>{submitErrors.sshKey}</ErrorMessage>}
                </ModalContent>
                <StyledModalFooter>
                  <ModalButton
                    variant="lightBlue"
                    size="small"
                    onClick={onClose}
                    disabled={submitting}
                    loading={submitting}
                  >
                    <FormattedMessage id="state.cancel" />
                  </ModalButton>
                  <ModalButton
                    variant="blue"
                    size="small"
                    type="submit"
                    disabled={pristine || hasValidationErrors || submitting}
                    loading={submitting}
                  >
                    <FormattedMessage
                      id={
                        createMode
                          ? 'merchant-integrations.sftp.form.create-user'
                          : 'merchant-integrations.sftp.form.save-ssh-key'
                      }
                    />
                  </ModalButton>
                </StyledModalFooter>
              </form>
            </>
          )}
        />
      </Container>
    </StyledModal>
  );
};

export default SftpUserKeysModal;
